import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import type {
  AlertNotifiedMessage,
  EventDto,
  EventListResponse,
  StreamMessage,
} from '@vision360/contracts';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { openStream, type StreamOptions, type StreamState } from '@/lib/api/stream';
import { useSession } from '@/lib/auth/session-context';
import { alertKeys, type EventFilter, matchesEventFilter, metricKeys } from '@/widgets/_sdk/hooks';

/**
 * One live stream per signed-in app (PRD VS-043, TechSpec §7.2). Messages are
 * buffered and applied to the Query cache at most once per second: new events
 * go into every cached event list they match, a metric batch invalidates that
 * source's metric queries (the server re-buckets), a status change refetches
 * the sources, an alert message refetches the alerts. A resync refetches
 * everything the stream feeds. Components read the connection state to show a
 * degraded indicator, never silently stale data. `alert.notified` (step 32) also
 * goes straight to its subscribers, so the toaster does not wait for the flush.
 */

export const FLUSH_MS = 1000;

const LiveStateContext = createContext<StreamState>('connecting');

export function useLiveState(): StreamState {
  return useContext(LiveStateContext);
}

type NotifiedListener = (message: AlertNotifiedMessage) => void;
const NotifiedContext = createContext<(listener: NotifiedListener) => () => void>(() => () => {});

/**
 * Step 32: calls `listener` for every `alert.notified` as it arrives, unbatched (a toast must
 * not wait for the flush). Pass a stable function (useCallback): it is resubscribed when it changes.
 */
export function useAlertNotified(listener: NotifiedListener): void {
  const subscribe = useContext(NotifiedContext);
  useEffect(() => subscribe(listener), [subscribe, listener]);
}

/** Applies a batch of stream messages to the cache. Pure over the client: testable. */
export function applyMessages(queryClient: QueryClient, messages: readonly StreamMessage[]): void {
  const events: EventDto[] = [];
  const metricSources = new Set<string>();
  let sourcesChanged = false;
  let alertsChanged = false;
  for (const message of messages) {
    if (message.type === 'event.created') events.push(message.data);
    else if (message.type === 'metric.batch') metricSources.add(message.data.sourceId);
    else if (message.type === 'source.status') sourcesChanged = true;
    else alertsChanged = true;
  }

  if (events.length > 0) {
    // Newest first, as the lists are.
    events.reverse();
    for (const query of queryClient.getQueryCache().findAll({ queryKey: ['events', 'list'] })) {
      const filter = query.queryKey[2] as EventFilter | undefined;
      if (!filter) continue;
      const matching = events.filter((e) => matchesEventFilter(filter, e));
      if (matching.length === 0) continue;
      queryClient.setQueryData<EventListResponse>(query.queryKey, (current) => {
        if (!current) return current;
        const ids = new Set(matching.map((e) => e.id));
        return {
          ...current,
          events: [...matching, ...current.events.filter((e) => !ids.has(e.id))].slice(
            0,
            filter.limit,
          ),
        };
      });
    }
  }
  for (const sourceId of metricSources) {
    void queryClient.invalidateQueries({ queryKey: metricKeys.source(sourceId) });
  }
  if (sourcesChanged) void queryClient.invalidateQueries({ queryKey: ['sources'] });
  if (alertsChanged) void queryClient.invalidateQueries({ queryKey: alertKeys.all });
}

export function LiveStreamProvider({
  children,
  open = openStream,
}: {
  children: ReactNode;
  open?: (options: StreamOptions) => () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [state, setState] = useState<StreamState>('connecting');
  const [listeners] = useState(() => new Set<NotifiedListener>());
  const [subscribe] = useState(() => (listener: NotifiedListener) => {
    listeners.add(listener);
    return () => void listeners.delete(listener);
  });

  useEffect(() => {
    let buffer: StreamMessage[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      timer = undefined;
      const batch = buffer;
      buffer = [];
      applyMessages(queryClient, batch);
    };

    const close = open({
      channels: ['events', 'sources', 'metrics', 'alerts'],
      accessToken: () => session.accessToken(),
      onState: setState,
      onResync: () => {
        buffer = [];
        void queryClient.invalidateQueries({ queryKey: ['events'] });
        void queryClient.invalidateQueries({ queryKey: ['sources'] });
        void queryClient.invalidateQueries({ queryKey: ['metrics'] });
        void queryClient.invalidateQueries({ queryKey: alertKeys.all });
      },
      onMessage: (message) => {
        if (message.type === 'alert.notified') for (const l of listeners) l(message.data);
        buffer.push(message);
        timer ??= setTimeout(flush, FLUSH_MS);
      },
    });
    return () => {
      clearTimeout(timer);
      close();
    };
  }, [open, session, queryClient, listeners]);

  return (
    <LiveStateContext.Provider value={state}>
      <NotifiedContext.Provider value={subscribe}>{children}</NotifiedContext.Provider>
    </LiveStateContext.Provider>
  );
}
